import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase.js';
import { regionCacheGet, regionCacheSet } from '../lib/regionCache.js';
import { regionSlug } from '../utils/regionSlug.js';
import { ANBAUGEBIETE } from '../data/regionsAndBereiche.js';

// Overview FeatureCollection's slot in the same IndexedDB store — region
// names can never collide with it.
const OVERVIEW_KEY = '__regions_overview__';

// Static-first path for anonymous readers: the same FeatureCollections
// the RPCs compute, baked at build time into /regions-data/ and served
// by the CDN (scripts/build_region_data.mjs). Returns null on any miss
// (404 in dev without a build, network error, shape mismatch) so the
// caller falls back to the live RPC.
async function fetchStatic(file, valid) {
  try {
    const res = await fetch(`/regions-data/${file}`);
    if (!res.ok) return null;
    const json = await res.json();
    return valid(json) ? json : null;
  } catch { return null; }
}

// Mode shape:
//   { kind: 'regions' }
//   { kind: 'unassigned' }
//   { kind: 'region', name: 'Mosel' }
//
// staticFirst (anonymous visitors): serve the baked CDN copy and keep the
// database out of the read path; signed-in users always hit the RPCs so
// their edits show immediately.

export function useMapData(mode, staticFirst = false) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqId = useRef(0);

  // dataVersion increments on every successful load, ensuring the
  // GeoJSON layer's key changes whenever new data arrives. This
  // forces React to unmount the old layer and mount a fresh one,
  // preventing stale data from being reused across mode transitions.
  const [dataVersion, setDataVersion] = useState(0);

  const load = useCallback(async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    setError(null);
    // Don't clear data — keep the previous view visible until new data arrives

    // Region views AND the overview: render the cached copy of the last
    // successful load immediately (fully interactive, "99% current") while
    // the fresh fetch runs. The guards keep a fresh result that lands first
    // from being overwritten by the slower cache read. Overview copies are
    // tagged `overview: true` (a region FC carries `region` instead).
    if (mode.kind === 'region') {
      regionCacheGet(mode.name).then(cached => {
        if (!cached || myReq !== reqId.current) return;
        let applied = false;
        setData(prev => {
          if (prev?.region === mode.name) return prev;
          applied = true;
          return cached;
        });
        if (applied) setDataVersion(v => v + 1);
      });
    } else if (mode.kind === 'regions') {
      regionCacheGet(OVERVIEW_KEY).then(cached => {
        if (!cached || myReq !== reqId.current) return;
        let applied = false;
        setData(prev => {
          if (prev?.overview) return prev;
          applied = true;
          return cached;
        });
        if (applied) setDataVersion(v => v + 1);
      });
    }

    try {
      let result;
      if (mode.kind === 'regions') {
        result = staticFirst
          // atlas guard: the baked static files are per-country — a stale
          // or foreign-atlas overview (German bake under a michigan dev
          // server) must fall through to the live RPC
          ? await fetchStatic('overview.json', fc => fc?.overview && fc.features?.length > 0
              && fc.features.every(f => ANBAUGEBIETE.includes(f.properties?.name)))
          : null;
        if (!result) {
          // Live from the DB: regions.geom is rebuilt from vineyard dots by
          // refresh_region_geoms() (manual refresh button), so the overview
          // blobs always match the data. Same shape as the old regions.json.
          const { data: rpc, error } = await supabase.rpc('regions_for_map');
          if (error) throw error;
          result = { ...rpc, overview: true };
        }
      } else if (mode.kind === 'unassigned') {
        // signed-in-only view: always live
        const { data: rpc, error } = await supabase.rpc('unassigned_vineyards_geojson');
        if (error) throw error;
        result = rpc;
      } else if (mode.kind === 'region') {
        result = staticFirst
          ? await fetchStatic(`${regionSlug(mode.name)}.json`, fc => fc?.region === mode.name && fc.features?.length > 0)
          : null;
        if (!result) {
          const { data: rpc, error } = await supabase.rpc('vineyards_by_region', {
            p_region: mode.name
          });
          if (error) throw error;
          result = rpc;
        }
      }
      // Discard if a newer request started while we were waiting
      if (myReq !== reqId.current) return;
      setDataVersion(v => v + 1);
      setData(result);
      if (mode.kind === 'region') regionCacheSet(mode.name, result);
      else if (mode.kind === 'regions') regionCacheSet(OVERVIEW_KEY, result);
    } catch (e) {
      if (myReq !== reqId.current) return;
      setError(e.message ?? String(e));
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [mode.kind, mode.name, staticFirst]);

  useEffect(() => { load(); }, [load]);

  // Patch one vineyard's feature into the in-memory FeatureCollection after a
  // save, instead of re-downloading the whole region. `feature` is the
  // vineyard_map_feature RPC result; null removes the feature (vineyard gone
  // or out of scope), as does an anbaugebiet that no longer matches the
  // current region. The dataVersion bump remounts the layer from local data —
  // no network round-trip.
  const patchFeature = useCallback((id, feature) => {
    if (mode.kind !== 'region') return;
    let applied = false;
    setData(prev => {
      // The RPC resolves async: the user may have left the region before it
      // lands, in which case `prev` is another view's FeatureCollection (the
      // regions overview has no `region` field). Only touch data that is
      // still this region's — mode.name is the region the save happened in.
      if (!prev?.features || prev.region !== mode.name) return prev;
      applied = true;
      const rest = prev.features.filter(f => f.properties?.id !== id);
      const keep = feature && feature.properties?.anbaugebiet === mode.name;
      return { ...prev, features: keep ? [...rest, feature] : rest };
    });
    setDataVersion(v => (applied ? v + 1 : v));
  }, [mode.kind, mode.name]);

  return { data, dataVersion, loading, error, refetch: load, patchFeature };
}
